import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { getExtraDetailsFields, parseExtraDetails, renderFieldValue } from './extraDetailsConfig';

interface ExtraDetailsDisplayProps {
  extraDetails: string;
  serviceName: string;
}

const ExtraDetailsDisplay: React.FC<ExtraDetailsDisplayProps> = ({ 
  extraDetails, 
  serviceName 
}) => {
  const parsedDetails = parseExtraDetails(extraDetails);
  const { fields } = getExtraDetailsFields(serviceName);

  if (!parsedDetails || Object.keys(parsedDetails).length === 0) {
    return null;
  }

  // Group fields by group name and filter out empty values
  const groupedFields = fields.reduce((groups, field) => {
    const value = parsedDetails[field.key];
    if (value === undefined || value === null || value === '') return groups;
    
    const groupName = field.group || 'General';
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(field);
    return groups;
  }, {} as Record<string, typeof fields>);

  if (Object.keys(groupedFields).length === 0) {
    return null;
  }

  return (
    <Box className="mt-1" 
    sx={{
      maxHeight: "300px",
      overflowY: "auto",
      pr: 1
    }}
    >
      {Object.entries(groupedFields).map(([groupName, groupFields]) => (
        <Box key={groupName} className="mb-1">
          {groupName !== 'General' && (
            <Typography 
              variant="subtitle1" 
              className="fw-bold mt-3 mb-1 text-dark"
              sx={{ fontWeight: 600 }}
            >
              {groupName}
            </Typography>
          )}
          <Grid container spacing={2}>
            {groupFields.map((field) => {
              const value = parsedDetails[field.key];
              return (
                <Grid item xs={12} sm={3} key={field.key}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '12px',
                      p: 2,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* Field Label */}
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 600, mb: 1 }}
                      color="text.secondary"
                    >
                      {field.label}
                    </Typography>

                    {/* Field Value as Chip */}
                    <Chip
                      label={renderFieldValue(value, field.type)}
                      sx={{
                        backgroundColor: '#ffe6eb',
                        color: '#d6336c',
                        fontWeight: 500,
                        borderRadius: '8px',
                        alignSelf: 'flex-start',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                        },
                        overflowX: "auto"
                      }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default ExtraDetailsDisplay;
